import { LoadingData } from "@senciamatch/frontend/components/LoadingData";
import { groupBy } from "@senciamatch/frontend/libs/array";
import { getJapaneseDayOfWeek } from "@senciamatch/frontend/libs/getJapaneseDayOfWeek";
import { trpc } from "@senciamatch/frontend/libs/trpc";
import { useWorkShiftEditStore } from "@senciamatch/frontend/stores/useWorkShiftEditStore";
import { useNavigate, useParams } from "react-router-dom";
import { v7 as uuidv7 } from "uuid";
import { Cell } from "./Cell";

export const PreviewPage = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { sessions, clearSessions } = useWorkShiftEditStore();

  // セッションを取得
  const session = sessions.find((s) => s.id === sessionId);

  // workShiftGroupIdから必要なデータを取得
  const workShiftGroupId = session?.workShiftGroupId;

  // mock-group-idの場合は、実際のworkShiftGroupIdを取得する
  const isMockData = workShiftGroupId === "mock-group-id";
  const getWorkShiftGroups = trpc.getWorkShiftGroups.useQuery({}, { enabled: isMockData });
  const actualWorkShiftGroupId =
    isMockData && getWorkShiftGroups.data?.workShiftGroups?.length
      ? getWorkShiftGroups.data.workShiftGroups[0].id
      : workShiftGroupId;

  const getWorkShiftGroup = trpc.getWorkShiftGroup.useQuery(
    { id: actualWorkShiftGroupId || "" },
    { enabled: !!actualWorkShiftGroupId },
  );
  const getWorkShifts = trpc.getStaffWorkShiftsByGroupId.useQuery(
    { workShiftGroupId: actualWorkShiftGroupId || "" },
    { enabled: !!actualWorkShiftGroupId },
  );

  if (!session) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold mb-4">セッションが見つかりません</h1>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
        >
          戻る
        </button>
      </div>
    );
  }

  if (getWorkShiftGroup.isPending || getWorkShifts.isPending || (isMockData && getWorkShiftGroups.isPending)) {
    return <LoadingData />;
  }

  if (!getWorkShiftGroup.data || !getWorkShifts.data) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold mb-4">データが見つかりません</h1>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
        >
          戻る
        </button>
      </div>
    );
  }

  const workShiftGroup = getWorkShiftGroup.data;
  const originalWorkShifts = getWorkShifts.data;

  // 変更を適用したworkShiftsを作成
  const modifiedWorkShifts = [...originalWorkShifts];
  session.changes.forEach((change) => {
    const index = modifiedWorkShifts.findIndex((ws) => ws.workShiftId === change.workShiftId);
    if (index !== -1) {
      modifiedWorkShifts[index] = {
        ...modifiedWorkShifts[index],
        staffId: change.newStaffId || modifiedWorkShifts[index].staffId,
        staffName: change.newStaffName || modifiedWorkShifts[index].staffName,
        staffShiftId: change.newStaffShiftId || modifiedWorkShifts[index].staffShiftId,
      };
    }
  });

  const workShiftsGroupedByBuildings = groupBy(
    modifiedWorkShifts.filter((ws) => ws.buildingId != null),
    (workShift) => workShift.buildingId as string,
  );

  const lastDayOfMonth = new Date(workShiftGroup.year, workShiftGroup.month, 0).getDate();

  // 変更されたworkShiftIdのセットを作成
  const modifiedWorkShiftIds = new Set(session.changes.map((c) => c.workShiftId));

  // シフト変更確定用のmutation
  const applyShiftChanges = trpc.simpleWorkShiftEdit.applyShiftChanges.useMutation({
    onSuccess: (data) => {
      console.log("applyShiftChanges success:", data);

      if (data.success) {
        alert(`${data.appliedCount}件の変更を適用しました`);
      } else {
        console.error("Partial failure:", data.errors);
        alert(
          `${data.appliedCount}件成功、${data.failedCount}件失敗しました\n詳細: ${data.errors?.map((e) => e.error).join(", ")}`,
        );
      }

      // セッションをクリアする
      clearSessions();
      // workshiftのshow画面へリダイレクト
      navigate(`/matchings/${session.workShiftGroupId}`);
    },
    onError: (error) => {
      console.error("applyShiftChanges error:", error);
      alert(`エラーが発生しました: ${error.message}`);
    },
  });

  // シフト変更確定処理
  const handleConfirmChanges = () => {
    if (session.changes.length === 0) {
      alert("変更がありません");
      return;
    }

    if (confirm(`${session.changes.length}件の変更を確定しますか？この操作は取り消せません。`)) {
      const changes = session.changes.map((change) => ({
        workShiftId: change.workShiftId,
        newStaffShiftId: change.newStaffShiftId,
      }));

      const payload = {
        workShiftGroupId: session.workShiftGroupId,
        changes,
      };

      console.log("Sending applyShiftChanges with payload:", payload);
      console.log("Session changes detail:", session.changes);

      applyShiftChanges.mutate(payload);
    }
  };

  return (
    <div className="h-full flex flex-col p-6">
      {/* ヘッダー */}
      <div className="flex-shrink-0 mb-6">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-bold">シフト変更プレビュー</h1>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
          >
            戻る
          </button>
        </div>
        <p className="text-gray-600">変更された箇所は緑色でハイライトされています。</p>
        <div className="text-sm text-gray-500 mt-2">
          変更件数: {session.changes.length}件 | 対象期間: {workShiftGroup.year}年{workShiftGroup.month}月
        </div>
      </div>

      {/* シフト表 */}
      <div className="flex-grow overflow-auto border rounded-lg min-h-0">
        <table className="w-full border-separate border-spacing-0">
          <thead>
            <tr>
              <th className="sticky top-0 left-0 bg-gray-100 p-2 border border-gray-200 z-40 min-w-[140px]">建物名</th>
              {Array.from({ length: lastDayOfMonth }, (_, i) => {
                const day = i + 1;
                const dayOfWeek = getJapaneseDayOfWeek(workShiftGroup.year, workShiftGroup.month, day);
                const bgColor = dayOfWeek === "日" ? "bg-red-100" : dayOfWeek === "土" ? "bg-blue-100" : "bg-gray-100";
                return (
                  <th key={uuidv7()} className={`sticky top-0 ${bgColor} p-2 border border-gray-200 z-10 w-[270px]`}>
                    {day}
                    <br />
                    {dayOfWeek}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {Object.entries(workShiftsGroupedByBuildings).map(([buildingId, workShifts]) => {
              return (
                <tr key={buildingId} className="border border-gray-200 h-20 bg-white">
                  <td className="sticky left-0 bg-white border z-20 border-gray-200 p-2 min-w-[140px]">
                    {workShifts[0].buildingName}
                  </td>
                  {Array.from({ length: lastDayOfMonth }, (_, i) => {
                    const workShift = workShifts.find((workShift) => workShift.day === i + 1);
                    const isModified = workShift && modifiedWorkShiftIds.has(workShift.workShiftId);
                    return <Cell key={uuidv7()} workShiftCell={workShift} isModified={isModified} />;
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* フッター */}
      <div className="flex-shrink-0 mt-6 flex justify-end gap-4">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
        >
          戻る
        </button>
        <button
          type="button"
          onClick={handleConfirmChanges}
          disabled={applyShiftChanges.isPending}
          className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {applyShiftChanges.isPending ? "処理中..." : "この変更で決定"}
        </button>
      </div>
    </div>
  );
};
